import * as Sentry from '@sentry/nextjs';
import AuthorQuote from 'components/authorquote';
import BotImage from 'components/botimage';
import Bubble from 'components/bubble';
import Button from 'components/buttons/button';
import { Petition } from 'components/cards';
import BotStatsCounters from 'components/counter/BotStatsCounters';
import { NationalCharts } from 'components/delivery-charts/Charts';
import Divider from 'components/divider';
import { JsonLd } from 'components/jsonLd';
import Layout from 'components/layout';
import Merch from 'components/merch';
import ProgressBar from 'components/progressBar';
import Anchor from 'components/typography/anchor';
import { useChatChannel } from 'hooks/useChatChannel';
import useMediaQuery from 'hooks/useMediaQuery';
import appStore from 'images/app-store.svg';
import appStoreDark from 'images/app-store-dark.svg';
import lovebot from 'images/bots/lovebot.svg';
import mailbot from 'images/bots/mailbot.svg';
import rosie from 'images/bots/rosie.svg';
import ruth from 'images/bots/ruth.svg';
import capitolLetters from 'images/capitol-letters.svg';
import capitolHero from 'images/capitol-only.svg';
import loopImg from 'images/loops.png';
import loopImgDark from 'images/loops-dark.png';
import arizonaRepublic from 'images/press/arizona-republic.svg';
import businessInsider from 'images/press/bi.svg';
import billboard from 'images/press/billboard.svg';
import bna from 'images/press/bloomberg.svg';
import bustle from 'images/press/bustle.svg';
import engadget from 'images/press/engadget.svg';
import fastCompany from 'images/press/fast-company.svg';
import good from 'images/press/good.svg';
import guardian from 'images/press/guardian.svg';
import huffingtonPost from 'images/press/huffington-post.svg';
import inc from 'images/press/inc.svg';
import inStyle from 'images/press/instyle.svg';
import kdka from 'images/press/kdka.svg';
import khou from 'images/press/khou.svg';
import lifehacker from 'images/press/lifehacker.svg';
import miamiHerald from 'images/press/miami-herald.svg';
import newsday from 'images/press/newsday.svg';
import nme from 'images/press/nme.svg';
import oprah from 'images/press/oprah.svg';
import recode from 'images/press/recode.svg';
import romper from 'images/press/romper.svg';
import salon from 'images/press/salon.svg';
import snopes from 'images/press/snopes.svg';
import teenVogue from 'images/press/teen-vogue.svg';
import theSkimm from 'images/press/theskimm.svg';
import wusa from 'images/press/wusa.svg';
import newsThumb from 'images/social-thumb.jpg';
import voteSeal from 'images/vote-seal.svg';
import voteSealDark from 'images/vote-seal-dark.svg';
import { getMerch } from 'lib/api/merch';
import { getPostsForHome } from 'lib/api/posts';
import { getSiteStats } from 'lib/api/stats';
import { getRepTotals, getTrendingPetitionsV2 } from 'lib/api/themis/queries';
import { HeartHandshake, MessagesSquare, PartyPopper } from 'lucide-react';
import Head from 'next/head';
import Img from 'next/image';
import PropTypes from 'prop-types';
import config from 'siteconfig';

import {
  POSTHOG_DONATE_CLICK_EVENT,
  POSTHOG_SIGNUP_EVENT_IMESSAGE,
  POSTHOG_SIGNUP_EVENT_SMS,
  POSTHOG_SIGNUP_EVENT_WEB_CHAT,
} from 'utilities/analytics';
import {
  BlogPostTileType,
  CampaignTypeList,
  MerchType,
  SiteStatsType,
} from 'utilities/propTypes';

const REVALIDATE_TIME_MINUTES = 5;
const REVALIDATE_TIME_SECONDS = REVALIDATE_TIME_MINUTES * 60;

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(
  async ({ preview = false }) => {
    const siteStatsQuery = getSiteStats({ preview });
    const postsQuery = getPostsForHome({ count: 3, preview });
    const trendingPetitionsQuery = getTrendingPetitionsV2({
      range: 'SEVEN_DAY',
      limit: 2,
    });
    const merchQuery = getMerch({ preview });
    const repTotalsQuery = getRepTotals();

    const responses = await Promise.all([
      siteStatsQuery,
      postsQuery,
      trendingPetitionsQuery,
      merchQuery,
      repTotalsQuery,
    ]);

    return {
      props: {
        siteStats: responses[0],
        posts: responses[1],
        trendingPetitions: responses[2],
        merch: responses[3],
        nationalData: responses[4],
        preview,
      },
      revalidate: REVALIDATE_TIME_SECONDS,
    };
  }
);

const Home = (props) => {
  const { merch, trendingPetitions, nationalData } = props;
  const {
    lettersSent,
    voterRegistrations,
    users,
    recurringDonors,
    targetRecurringDonors,
  } = props.siteStats;

  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const {
    supportsAppleMessages,
    isDeepLink,
    webChatHref,
    href: startChatHref,
    eventName: startChatEvent,
  } = useChatChannel('resist');

  return (
    <Layout
      title="Resistbot — Write to Your Elected Officials in Minutes"
      description="Write to your senators, representatives, and governor in minutes. Text RESIST to 50409 or chat on the web. Resistbot delivers your message via email, fax, or postal mail. 40 million letters sent."
      canonicalPath="/"
    >
      <Head>
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How do I write a letter to my congressman?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Text RESIST to 50409 or visit resist.bot. Answer a few questions and Resistbot delivers your letter to all your elected officials via fax, email, or postal mail in minutes.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is Resistbot free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Writing and sending letters is free. Premium members get additional features like postal mail delivery, campaign coins, and voter registration monitoring.',
                },
              },
              {
                '@type': 'Question',
                name: 'Does writing your congressman actually work?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "Yes. Resistbot has delivered over 40 million letters. Academic research shows Resistbot's outreach increased voter turnout by up to 2 percentage points.",
                },
              },
              {
                '@type': 'Question',
                name: 'How does Resistbot work?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Text RESIST to 50409 or use the web chat. Resistbot finds your representatives automatically, helps you draft a letter or sign an existing petition, and delivers it via fax, email, or postal mail in minutes.',
                },
              },
            ],
          }}
        />
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Resistbot',
            url: 'https://resist.bot',
            logo: 'https://resist.bot/favicons/icon-512x512.png',
            description:
              'Resistbot turns your texts into letters to your elected officials, delivered in minutes.',
            sameAs: [
              'https://bsky.app/profile/resist.bot',
              'https://www.instagram.com/resistbot/',
              'https://www.threads.net/@resistbot',
            ],
            foundingDate: '2017',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer support',
              url: 'https://resist.bot/about',
            },
          }}
        />
      </Head>
      <div className="flex flex-col gap-4 pb-10 text-center">
        <Img
          src={capitolLetters}
          fill={true}
          alt="An illustration of letters flying at the U.S. Capitol"
          priority={true}
          className="-z-10 dark:opacity-20 dark:mix-blend-color-dodge"
        />
        <h1 className="tracking-tight sm:text-3xl">
          Write to Your Elected Officials in&nbsp;Minutes
        </h1>
        <section className="relative h-[36vw] max-h-96">
          <Img
            src={capitolHero}
            alt="An illustration of the U.S. Capitol complex"
            priority={true}
            fill={true}
            className=""
          />
        </section>
        <p className="text-xl tracking-tight">
          One message can reach your senators, representative, governor, state
          legislature, mayor—or all of them at once—by email, fax, or
          postal&nbsp;mail.
        </p>
        <div className="mx-auto flex flex-wrap justify-center gap-2">
          <Button
            theme="animated"
            size="large"
            href={startChatHref}
            eventName={startChatEvent}
          >
            <MessagesSquare size={24} strokeWidth={2} className="opacity-70" />
            Chat <span className="hidden sm:inline">with Resistbot</span>
          </Button>
          <Button
            theme="light"
            size="large"
            href={config.donateUrls.stripe}
            eventName={POSTHOG_DONATE_CLICK_EVENT}
            eventData={{ placement: 'hero' }}
          >
            <HeartHandshake size={24} strokeWidth={2} className="opacity-70" />
            Donate
          </Button>
          {isDeepLink ? (
            <p className="basis-full text-sm font-light text-stone-500">
              {supportsAppleMessages
                ? 'Opens in Messages.'
                : 'Opens your texts.'}{' '}
              <Anchor
                className="anchor-subtle font-bold"
                href={webChatHref}
                eventName={POSTHOG_SIGNUP_EVENT_WEB_CHAT}
              >
                Chat on the web
              </Anchor>{' '}
              instead.
            </p>
          ) : null}
        </div>
      </div>
      <section className="mx-auto flex max-w-prose flex-col flex-nowrap text-center">
        <BotImage src={rosie.src} priority={true} />

        <h2>How It&nbsp;Works</h2>
        <p className="mb-4">
          Text <Bubble>resist</Bubble> to{' '}
          {supportsAppleMessages ? (
            <Anchor
              className="anchor-text"
              href={`${config.channelUrls.apple}resist`}
              eventName={POSTHOG_SIGNUP_EVENT_IMESSAGE}
            >
              Apple Messages
            </Anchor>
          ) : (
            <Anchor
              className="anchor-text"
              href={`${config.channelUrls.sms}resist`}
              eventName={POSTHOG_SIGNUP_EVENT_SMS}
            >
              50409
            </Anchor>
          )}{' '}
          or use our{' '}
          <Anchor className="anchor-text" href="/chat">
            web bot
          </Anchor>
          . Answer a few questions, and we’ll deliver your message to all your
          elected officials in&nbsp;minutes.
        </p>

        <p className="text-xs">
          Message and data rates may apply if you use SMS. Message frequency
          varies. Text STOP to 50409 to stop all messages. Text HELP to 50409
          for help. See our{' '}
          <Anchor href="/terms" className="anchor-subtle">
            terms&nbsp;of&nbsp;use
          </Anchor>
          ,{' '}
          <Anchor href="/privacy" className="anchor-subtle">
            privacy&nbsp;notice
          </Anchor>
          , and{' '}
          <Anchor href="/rights" className="anchor-subtle">
            user bill of&nbsp;rights
          </Anchor>
          .
        </p>
        <div className="iphone-container">
          <p className="max-w-56 rounded-lg bg-white p-4 text-xs text-black shadow-sm dark:bg-stone-800 dark:text-white">
            <span role="img" aria-label="Newspaper">
              📰
            </span>{' '}
            Send me an article to teach me about your issue!
          </p>
          <p className="max-w-48 self-end overflow-hidden rounded-lg bg-white text-xs text-black shadow-sm dark:bg-stone-800 dark:text-white">
            <Img
              alt="Photograph of Louis Dejoy"
              src={newsThumb}
              priority={false}
              height={160}
              width={240}
            />

            <span className="block p-4 pb-0">
              DeJoy’s 10-Year Plan Could Gut USPS. He Doesn’t Want You to Know
              the Details.
            </span>
            <span className="block p-4 pt-0 text-stone-500 dark:text-stone-400">
              truthout.org
            </span>
          </p>
          <p className="max-w-56 rounded-lg bg-white p-4 text-xs text-black shadow-sm dark:bg-stone-800 dark:text-white">
            <span role="img" aria-label="Sparkles">
              ✨
            </span>{' '}
            Got it, here’s your letter:
            <br />
            <br />
            The U.S. Postal Service’s 10-year plan, led by Postmaster General
            Louis DeJoy, raises significant concerns. The proposed consolidation
            of mail sorting and delivery into regional centers threatens smaller
            post offices with potential closure or reduced hours. This lack of
            transparency and resistance…
          </p>
        </div>
        <div>
          <h3>No Research, No Writing Required</h3>
          <p>
            Resistbot finds your representatives automatically. Paste any bill
            number or news article and we’ll draft your letter&mdash;or write
            your own from scratch.
          </p>
        </div>

        <AuthorQuote
          author="@zombiezeni"
          position="Resistbot Member"
          href="https://www.tiktok.com/@zombiezeni/video/7214222550015020330"
        >
          Raise your hand if you would rather go get a cavity filled than make
          four calls and leave four voicemails… thank goodness there is a
          better&nbsp;way.
        </AuthorQuote>
      </section>
      <Divider />
      <section className="flex flex-col flex-nowrap gap-4 text-center">
        <BotImage src={mailbot.src} />
        <div>
          <h2>Join a Movement</h2>
          <p>
            Not sure what to say? Sign one of our trending campaigns and we’ll
            deliver your message&nbsp;instantly.
          </p>
        </div>

        <div className="mb-10 flex flex-col gap-1">
          {trendingPetitions.map((petition) => (
            <Petition key={petition.petition_code} petition={petition} />
          ))}

          <Button theme="outline" href={`/petitions`}>
            See Trending Campaigns
          </Button>
        </div>
        <div>
          <h3>Or Start Your&nbsp;Own</h3>
          <p className="mx-auto max-w-prose">
            Ready to lead? Turn any{' '}
            <Anchor className="anchor-text" href="/letters">
              letter you write
            </Anchor>{' '}
            into{' '}
            <Anchor className="anchor-text" href="/petitions">
              a campaign
            </Anchor>{' '}
            that reaches&nbsp;thousands. Text <Bubble>campaign</Bubble>{' '}
            to&nbsp;start.
          </p>
        </div>

        <div className="relative h-[50vw] max-h-96">
          {prefersDark ? (
            <Img
              src={loopImgDark}
              alt="Diagram of how Resistbot campaigns grow to create more signers and more deliveries over time"
              fill
              style={{ objectFit: 'contain' }}
            />
          ) : (
            <Img
              src={loopImg}
              alt="Diagram of how Resistbot campaigns grow to create more signers and more deliveries over time"
              fill
              style={{ objectFit: 'contain' }}
            />
          )}
        </div>

        <ol className="flex flex-wrap gap-4 text-left text-sm">
          <li
            className="basis-[calc(50%-16px)] before:mb-1 before:block before:size-6 before:rounded-full before:bg-pink-500 before:text-center before:leading-6 before:font-extrabold before:text-white before:content-[attr(data-counter)] sm:basis-[calc(25%-16px)]"
            data-counter="1"
          >
            <h3 className="text-base font-bold text-stone-900 font-semi-expanded dark:text-stone-100">
              Launch
            </h3>
            <p>
              Campaigns start with <strong>announcement texts</strong> to
              your followers.
            </p>
          </li>
          <li
            className="basis-[calc(50%-16px)] before:mb-1 before:block before:size-6 before:rounded-full before:bg-purple-500 before:text-center before:leading-6 before:font-extrabold before:text-white before:content-[attr(data-counter)] sm:basis-[calc(25%-16px)]"
            data-counter="2"
          >
            <h3 className="text-base font-bold text-stone-900 font-semi-expanded dark:text-stone-100">
              Instant&nbsp;Delivery
            </h3>
            <p>
              Each sign drives <strong>letters</strong> to the officials that
              represent the signer.
            </p>
          </li>
          <li
            className="basis-[calc(50%-16px)] before:mb-1 before:block before:size-6 before:rounded-full before:bg-blue-500 before:text-center before:leading-6 before:font-extrabold before:text-white before:content-[attr(data-counter)] sm:basis-[calc(25%-16px)]"
            data-counter="3"
          >
            <h3 className="text-base font-bold text-stone-900 font-semi-expanded dark:text-stone-100">
              Amplify
            </h3>
            <p>
              Signers <strong>spend coins</strong> to promote your campaign,{' '}
              <strong>share</strong> & <strong>invite</strong>&nbsp;friends.
            </p>
          </li>
          <li
            className="basis-[calc(50%-16px)] before:mb-1 before:block before:size-6 before:rounded-full before:bg-teal-300 before:text-center before:leading-6 before:font-extrabold before:text-white before:content-[attr(data-counter)] sm:basis-[calc(25%-16px)]"
            data-counter="4"
          >
            <h3 className="text-base font-bold text-stone-900 font-semi-expanded dark:text-stone-100">
              Grow Organizing&nbsp;Power
            </h3>
            <p>
              You <strong>gain followers</strong> and{' '}
              <strong>earn coins</strong> spent on promoting your
              campaign—expanding your influence with every run.
            </p>
          </li>
        </ol>

        <div className="mt-8 flex flex-col flex-nowrap gap-8 sm:flex-row sm:gap-4">
          <AuthorQuote
            author="Ben Wikler"
            position="Chair"
            station="Wisc. Democratic Party"
            className="flex-1"
          >
            Resistbot has an unparalleled and unprecedented power to move
            thousands upon thousands of people to action&nbsp;—&nbsp;instantly.
          </AuthorQuote>

          <AuthorQuote
            author="Kit Wu"
            position="The movement to repeal 50-a in N.Y."
            href="https://medium.com/visible-hands/from-zero-to-cuomos-desk-in-two-weeks-237dd93c894a"
            className="flex-1"
          >
            The final count of constituents who sent an email to their state
            legislator was close to 146,000, and we wouldn’t have been anywhere
            close to that were it not for your efforts… This campaign would not
            [have been] possible without&nbsp;Resistbot.
          </AuthorQuote>
        </div>
      </section>
      <Divider />
      <section className="mx-auto flex max-w-prose flex-col flex-nowrap text-center">
        <BotImage src={lovebot.src} priority={false} />

        <h2>Unlock a More Powerful&nbsp;Resistbot</h2>
        <p className="mb-4">
          Join to earn coins for your{' '}
          <Anchor className="anchor-subtle" href="/petitions">
            campaigns
          </Anchor>
          , a 100% coin bonus per dollar,{' '}
          <Anchor className="anchor-subtle" href="/go/jesscraven101">
            your own keyword
          </Anchor>{' '}
          for persistent organizing, physical delivery of letters via postal
          mail or fax, daily newspaper front pages, and{' '}
          <Anchor className="anchor-subtle" href="/premium">
            much&nbsp;more
          </Anchor>
          . We rely on your membership,{' '}
          <Anchor className="anchor-subtle" href="/rights">
            not your data
          </Anchor>
          , to&nbsp;operate.
        </p>

        <Button theme="animated" href="/premium" size="full" className="mb-4">
          <PartyPopper size={24} strokeWidth={2} className="opacity-70" />
          Become a <span className="hidden sm:inline">Premium</span> Member
        </Button>
        <ProgressBar
          current={recurringDonors}
          tiers={[targetRecurringDonors]}
        />

        <AuthorQuote
          author="Democrats Abroad"
          href="https://www.democratsabroad.org/activism_from_abroad_resistbot"
          className="mt-4"
        >
          Resistbot is a well-designed, powerful easy-to-use tool that allows
          you to communicate your thoughts, wishes, complaints,
          congratulations&mdash;anything that you care to share&mdash;to your
          elected&nbsp;representatives.
        </AuthorQuote>
      </section>
      <Divider />
      <section className="mx-auto flex flex-col flex-nowrap text-center">
        <h2>We Can Do&nbsp;It!</h2>
        <p className="mb-4">
          Forty million{' '}
          <Anchor className="anchor-subtle" href="/letters">
            letters
          </Anchor>{' '}
          delivered and counting. We’re also proud to have published tens of
          thousands of them in{' '}
          <Anchor className="anchor-subtle" href="/letters-to-the-editor">
            local newspapers in 49 states and&nbsp;D.C.
          </Anchor>
        </p>

        <NationalCharts data={nationalData} />
        <BotStatsCounters {...{ lettersSent, users, voterRegistrations }} />
      </section>

      <section className="mx-auto flex max-w-prose flex-col flex-nowrap gap-4 text-center">
        {prefersDark ? (
          <Img
            src={voteSealDark}
            alt="Diagram of how Resistbot campaigns grow to create more signers and more deliveries over time"
            height="200"
            width="200"
            className="mx-auto"
          />
        ) : (
          <Img
            src={voteSeal}
            alt="Diagram of how Resistbot campaigns grow to create more signers and more deliveries over time"
            height="200"
            width="200"
            className="mx-auto"
          />
        )}

        <p>
          Text <Bubble>Check</Bubble> to Resistbot and we will protect your
          registration, for free, as long as we stay online. This feature
          launched in 2018 and we’re still the only organization that does it,
          in the face of{' '}
          <Anchor
            className="anchor-subtle"
            href="https://www.democracydocket.com/analysis/in-seven-states-removing-voters-from-the-rolls-just-got-easier/"
          >
            endless voter roll purges
          </Anchor>{' '}
          across the&nbsp;country.
        </p>

        <AuthorQuote
          author="Jess Craven"
          position="Chop Wood, Carry Water"
          href="https://www.tiktok.com/@jesscraven101/video/7151808543249747246"
        >
          Not only will Resistbot check your voter registration… once they have
          confirmed you are registered to vote they will check it once a month
          to make sure that you are not purged from the voter rolls and if
          anything changes they will send you a text… I think everyone should
          use&nbsp;it.
        </AuthorQuote>
      </section>
      <Divider />
      <section className="flex flex-col flex-nowrap text-center">
        <BotImage src={ruth.src} />
        <h2>Real World Impact</h2>
        <p className="mx-auto max-w-prose">
          Our impact was proven in three studies across 11 elections. Your
          dollar goes farther with us than any other turnout program.{' '}
          <Anchor className="anchor-text" href="/premium">
            Become a member
          </Anchor>{' '}
          or{' '}
          <Anchor
            className="anchor-text"
            href={config.donateUrls.stripe}
            eventName={POSTHOG_DONATE_CLICK_EVENT}
            eventData={{ placement: 'impact_body' }}
          >
            donate
          </Anchor>{' '}
          to support our&nbsp;work.
        </p>
        <div className="iphone-container my-6">
          <p className="max-w-56 rounded-lg bg-white p-4 text-xs text-black shadow-sm dark:bg-stone-800 dark:text-white">
            <span role="img" aria-label="Reminder Ribbon">
              🎗️
            </span>{' '}
            You have an election next Tuesday! You pledged your support to
            Sherrod Brown. Do you need to know where your polling place is?
          </p>
          <p className="max-w-56 self-end rounded-lg bg-blue-600 p-4 text-xs text-white shadow-sm">
            <span role="img" aria-label="Thumbs Up">
              👍
            </span>{' '}
            Yes, please!
          </p>
          <p className="max-w-56 rounded-lg bg-white p-4 text-xs text-black shadow-sm dark:bg-stone-800 dark:text-white">
            <span role="img" aria-label="Ballot Box with Ballot">
              🗳️
            </span>{' '}
            I found 8 early voting sites, 3 polling places for Election Day, and
            15 drop boxes, which do you want to see?
          </p>
        </div>
        <div className="flex flex-col flex-nowrap gap-8 sm:flex-row sm:gap-4">
          <AuthorQuote
            author="Analyst Institute"
            href="https://resistbot-assorted-public.s3-us-west-2.amazonaws.com/analyst-institute.pdf"
          >
            Resistbot’s GOTV program was highly cost-effective and able to
            generate an impressive number of net voters, surpassing the
            performance of many other programs in midterm election&nbsp;cycles.
          </AuthorQuote>
          <AuthorQuote
            author="Christopher B. Mann, Ph.D."
            href="https://resistbot-assorted-public.s3-us-west-2.amazonaws.com/wisconsin-gotv.pdf"
          >
            [Resistbot] increased turnout by 1.8 percentage points in a 2019
            election
          </AuthorQuote>
          <AuthorQuote
            author="Christopher B. Mann, Ph.D. & Scott L. Minkoff, Ph.D."
            href="https://resistbot-assorted-public.s3.us-west-2.amazonaws.com/2019-2020-turnout-analysis.pdf"
          >
            With an estimated treatment effect of… as much as 2 percentage
            points in 2020… messaging [Resistbot’s] user base can boost turnout.
          </AuthorQuote>
        </div>
      </section>
      <Divider />
      <section className="mx-auto flex flex-col flex-nowrap gap-4 text-center">
        <h2>We’ve Met the&nbsp;Press</h2>
        <div className="flex flex-wrap place-content-evenly items-center justify-around gap-6 dark:invert">
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.recode.net/2017/3/9/14865882/resistbot-texts-faxes-senators-representatives"
          >
            <Img src={recode} alt="Recode" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.teenvogue.com/story/resistbot-faxes-texts-to-senators"
          >
            <Img src={teenVogue} alt="Teen Vogue" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.engadget.com/2017/03/09/resistbot/"
          >
            <Img src={engadget} alt="Engadget" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.fastcompany.com/90470629/why-resistbot-partnered-with-kind-snacks-to-promote-the-equal-rights-amendment"
          >
            <Img src={fastCompany} alt="Fast Company" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.theguardian.com/us-news/2017/mar/24/the-resistance-now-newsletter-resistbot-healthcare-bill"
          >
            <Img src={guardian} alt="The Guardian" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.huffingtonpost.com/entry/resistbot-faxes-politicians_us_58d8a426e4b03692bea70829"
          >
            <Img src={huffingtonPost} alt="The Huffington Post" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.good.is/articles/anti-trump-tech"
          >
            <Img src={good} alt="Good" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.bustle.com/p/resistbot-makes-contacting-your-representatives-easier-than-ever-before-47570"
          >
            <Img src={bustle} alt="Bustle" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://lifehacker.com/text-your-members-of-congress-with-resistbot-1794604660"
          >
            <Img src={lifehacker} alt="Lifehacker" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.romper.com/p/how-to-text-your-senator-about-the-zero-tolerance-policy-because-you-need-to-do-something-9509585"
          >
            <Img src={romper} alt="Romper" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.businessinsider.com/resistbot-overloaded-users-fax-congress-about-gop-healthcare-2017-9"
          >
            <Img src={businessInsider} alt="Business Insider" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.bna.com/wanna-join-trump-b57982085087/"
          >
            <Img src={bna} alt="Bloomberg BNA" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="http://www.miamiherald.com/news/nation-world/national/article189513934.html"
          >
            <Img src={miamiHerald} alt="Miami Herald" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://theskimm.com/archive/2018-03-26"
          >
            <Img src={theSkimm} alt="The Skimm" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="http://www.wusa9.com/article/news/local/verify/verify-text-reveals-nra-political-contributions-to-elected-officials/65-522285283"
          >
            <Img src={wusa} alt="WUSA TV" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="http://pittsburgh.cbslocal.com/2017/05/03/pitt-grad-resistbot-congress/"
          >
            <Img src={kdka} alt="KDKA TV" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.instyle.com/news/how-you-can-make-your-voice-heard-amid-kavanaugh-investigation"
          >
            <Img src={inStyle} alt="InStyle" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.azcentral.com/story/news/politics/arizona/2019/05/17/app-called-resistbot-helps-critics-put-heat-ariz-republicans-martha-mcsally-david-schweikert/3707551002/"
          >
            <Img src={arizonaRepublic} alt="Arizona Republic" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.newsday.com/news/nation/impeachment-house-democrats-republicans-trump-w74605"
          >
            <Img src={newsday} alt="Newsday" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.billboard.com/music/pop/billie-eilish-launches-voting-initiative-9459092/"
          >
            <Img src={billboard} alt="Billboard" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.nme.com/news/music/billie-eilish-on-responsibility-to-discuss-politics-you-cant-ignore-it-and-you-cant-be-silent-2774478"
          >
            <Img src={nme} alt="NME" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.inc.com/minda-zetlin/americans-support-equal-rights-amendment-3-to-1.html"
          >
            <Img src={inc} alt="Inc." fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.snopes.com/fact-check/usps-50409/"
          >
            <Img src={snopes} alt="Snopes" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.khou.com/article/news/verify/verify-texting-usps-to-50409-will-send-letters-to-elected-officials/285-284bbb34-f3b5-40e7-8605-1674fdddd104"
          >
            <Img src={khou} alt="KHOU" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.oprah.com/inspiration/how-to-call-senators-representatives"
          >
            <Img src={oprah} alt="O Magazine" fill />
          </Anchor>
          <Anchor
            className="relative h-6 w-4/12 max-w-32 opacity-40 transition-opacity hover:opacity-100"
            href="https://www.salon.com/2020/09/24/text-messages-are-the-new-door-knocking--but-voters-are-paranoid-about-political-outreach-via-text/"
          >
            <Img src={salon} alt="Salon" fill />
          </Anchor>
        </div>
      </section>
      <Divider />
      <section className="flex flex-col flex-nowrap text-center">
        <h2>Drive More Impact with the&nbsp;App</h2>

        <p className="mx-auto max-w-prose">
          Get the{' '}
          <Anchor className="anchor-text" href={config.miscUrls.iosApp}>
            iOS app
          </Anchor>{' '}
          to send &amp; drive deliveries of{' '}
          <Anchor className="anchor-text" href="/petitions">
            top&nbsp;campaigns
          </Anchor>
          .
        </p>
        <div className="iphone-container gap-2 px-10">
          <Anchor href={config.miscUrls.iosApp}>
            <div className="rounded-sm bg-linear-to-tr from-indigo-500 to-purple-500 p-4 text-white shadow-sm duration-300 hover:shadow-lg">
              <p className="text-xl">20,680</p>
              <p className="text-xs tracking-widest text-white/50 uppercase">
                signers
              </p>
              <p className="text-base leading-snug font-bold tracking-tight">
                Vote Yes for Jan. 6th Commission
              </p>
              <p className="line-clamp-4 text-xs text-white/50">
                We urge you to support a 9/11-style Commission charged with
                investigating and reporting on the facts and causes relating to
                the January 6, 2021, attack on the United States Capitol which
                sought to interrupt the peaceful transfer of power. The
                Commission’s mandate should include investigation of the facts
              </p>
            </div>
          </Anchor>
          <Anchor href={config.miscUrls.iosApp}>
            <div className="rounded-sm bg-linear-to-tr from-indigo-500 to-purple-500 p-4 text-white shadow-sm duration-300 hover:shadow-lg">
              <p className="text-xl">5,120</p>
              <p className="text-xs tracking-widest text-white/50 uppercase">
                signers
              </p>
              <p className="text-base leading-snug font-bold tracking-tight">
                Abolish the Filibuster
              </p>
              <p className="line-clamp-4 text-xs text-white/50">
                The fillibuster is antidemocratic. At this point it stands
                directly in the way of voting rights and national security by
                blocking the bill to investigate the 1/6 insurrection. There is
                neither a political nor an ideological reason to maintain it.
                Democrats have gone out of their way in the interest of
                bipartisan cooperation for the creation of a commission to
                investigate the insurrection.
              </p>
            </div>
          </Anchor>
        </div>
        <Anchor href={config.miscUrls.iosApp}>
          {prefersDark ? (
            <Img
              src={appStoreDark}
              alt="Download on the App Store"
              height={40}
              width={120}
              style={{ display: 'block', margin: '0 auto' }}
            />
          ) : (
            <Img
              src={appStore}
              alt="Download on the App Store"
              height={40}
              width={120}
              style={{ display: 'block', margin: '0 auto' }}
            />
          )}
        </Anchor>
      </section>
      <Divider />
      <section>
        <Merch items={merch} />
      </section>
      <Divider />
      <section className="flex flex-col flex-nowrap items-center text-center">
        <h2>Give it a try!</h2>
        <p className="mx-auto mb-4 max-w-prose">
          Resistbot has been trusted to be the easiest and quickest way to lobby
          your elected officials since 2017. No web forms to deal with, and
          instant delivery to multiple officials&nbsp;simultaneously.
        </p>
        <div className="mx-auto flex flex-wrap items-center justify-center gap-2">
          <Button
            theme="animated"
            size="large"
            href={startChatHref}
            eventName={startChatEvent}
          >
            <MessagesSquare size={24} strokeWidth={2} className="opacity-70" />
            Chat <span className="hidden sm:inline">with Resistbot</span>
          </Button>
          <Button
            theme="light"
            size="large"
            href={config.donateUrls.stripe}
            eventName={POSTHOG_DONATE_CLICK_EVENT}
            eventData={{ placement: 'footer' }}
          >
            <HeartHandshake size={24} strokeWidth={2} className="opacity-70" />
            Donate
          </Button>
          {isDeepLink ? (
            <p className="basis-full text-sm font-light text-stone-500">
              {supportsAppleMessages
                ? 'Opens in Messages.'
                : 'Opens your texts.'}{' '}
              <Anchor
                className="anchor-subtle font-bold"
                href={webChatHref}
                eventName={POSTHOG_SIGNUP_EVENT_WEB_CHAT}
              >
                Chat on the web
              </Anchor>{' '}
              instead.
            </p>
          ) : null}
        </div>
      </section>
    </Layout>
  );
};

Home.propTypes = {
  merch: PropTypes.arrayOf(MerchType),
  posts: PropTypes.arrayOf(BlogPostTileType),
  trendingPetitions: PropTypes.arrayOf(CampaignTypeList),
  siteStats: SiteStatsType,
};

export default Home;
